<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import DocumentFormSidebar from './components/DocumentFormSidebar.vue'
import DocumentSearchText from '@/modules/documents-new/components/DocumentSearchText.vue'
import DocumentMoveModal from './components/modals/DocumentMoveModal.vue'
import ColumnSettingsModal from './components/modals/ColumnSettingsModal.vue'
import DocumentTypesManageModal from './components/modals/DocumentTypesManageModal.vue'
import FolderNameModal from './components/modals/FolderNameModal.vue'
import DateFilterDropdown from './components/DateFilterDropdown.vue'
import NumberFilterDropdown from './components/NumberFilterDropdown.vue'
import FolderTree from './components/FolderTree.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import {
  type FilterChip,
  useDocuments,
  useFilters,
  useFolders,
  useModal,
} from '@/modules/documents-new'
import { useColumnVisibility } from '@/modules/documents-new/composables/useColumnVisibility'
import { DocumentsApi } from '@/modules/documents-new/services/documentsApi'
import { FoldersApi } from '@/modules/documents-new/services/foldersApi'
import { useDocumentsFilterStore } from '@/modules/documents-new/stores/documentsFilterStore'
import { useStaffSettingsPane } from '@/modules/staff/composables/useStaffSettingsPane'
import {
  getDocumentStatusConfig,
  getDocumentStatusLabel,
} from '@/modules/documents-new/constants/documentStatuses'
import type { FolderItem } from '@/modules/documents-new/types/folder.types'
import { type DocumentItem, getDocumentEdition } from '@/modules/documents-new/types/document.types'
import { pluralize } from '@/modules/documents-new/utils/pluralize'
import { useNotifications } from '@/modules/feedback/composables/useNotifications'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import ImportHistoryModal from '@/modules/import/components/ImportHistoryModal.vue'
import { ERouteNames } from '@/router/ERouteNames'
import { formatDateOnly } from '@/utils/formatters'
import { useTableHideCompleted } from '@/composables/useTableHideCompleted'
import type { DocumentTreeItem } from '@/modules/documents-new/types/tree.types'

const route = useRoute()
const router = useRouter()
const { width } = useDisplay()
const theme = useTheme()

const isSidebarOpen = ref(true)
const isMobile = computed(() => width.value < 1024)

const {
  width: sidebarPaneWidth,
  startResize: startSidebarResize,
  isResizing: isSidebarResizing,
} = useStaffSettingsPane({
  initial: 360,
  min: 240,
  max: () => Math.max(480, window.innerWidth - 480),
  storageKey: 'documents-folders-left',
})

const { showSuccess, showError } = useNotifications()
const permissionsStore = usePermissionsStore()

const canEditDocuments = computed(() => !!permissionsStore.modules.documents?.edit)
const isDocumentTypeModalOpen = ref(false)

const isSmallScreen = computed(() => width.value < 640)

const {
  folders,
  rootFolder,
  selectedFolderId,
  selectedFolder,
  localFolder,
  localFolderId,
  globalFolderId,
  libraryRootId,
  parentFolderId,
  isInGlobalLibrary,
  isLoading: isFoldersLoading,
  error: foldersError,
  loadFolders,
  toggleFolder,
  toggleRootFolder,
  expandRootFolder,
  selectFolder,
  findFolder,
  isLibraryRootId,
  isFolderInGlobalLibrary,
  canCreateFolderIn,
  canEditFolder,
} = useFolders()

const PARENT_NAV_ID = '__parent__'

const DOCUMENT_QUERY_KEY = 'document'
const LOCAL_FOLDER_SLUG = 'local'
const GLOBAL_FOLDER_SLUG = 'global'

const folderIdParam = computed(() => {
  const p = route.params.folderId
  if (!p) {
    return null
  }

  return String(Array.isArray(p) ? p[0] : p)
})

/** ID папки из URL: строковый UUID или slug local/global */
const folderId = computed(() => {
  const raw = folderIdParam.value
  if (!raw) {
    return null
  }

  const slug = raw.trim().toLowerCase()
  if (slug === LOCAL_FOLDER_SLUG) {
    return localFolderId.value
  }
  if (slug === GLOBAL_FOLDER_SLUG) {
    return globalFolderId.value
  }

  return raw.trim()
})

watch(
  folderId,
  id => {
    if (id != null) {
      selectFolder(id)
    }
  },
  { immediate: true },
)

function parseDocumentId(value: unknown): string | null {
  if (value == null || value === '') {
    return null
  }

  const raw = String(value).trim()
  return raw || null
}

/** ID карточки из URL — по этой ссылке сразу открывается нужный документ */
const documentIdFromRoute = computed(() => {
  const raw = route.query[DOCUMENT_QUERY_KEY]
  const value = Array.isArray(raw) ? raw[0] : raw

  return parseDocumentId(value)
})

function folderIdToRouteParam(targetFolderId: string): string {
  if (localFolderId.value != null && String(targetFolderId) === String(localFolderId.value)) {
    return LOCAL_FOLDER_SLUG
  }
  if (globalFolderId.value != null && String(targetFolderId) === String(globalFolderId.value)) {
    return GLOBAL_FOLDER_SLUG
  }

  return targetFolderId
}

function buildDocumentsLocation(targetFolderId: string | null, documentId: string | null) {
  const query = { ...route.query }
  if (documentId != null) {
    query[DOCUMENT_QUERY_KEY] = documentId
  } else {
    delete query[DOCUMENT_QUERY_KEY]
  }

  if (targetFolderId == null) {
    return { name: ERouteNames.DOCUMENTS, query }
  }

  return {
    name: ERouteNames.DOCUMENTS_FOLDER,
    params: { folderId: folderIdToRouteParam(targetFolderId) },
    query,
  }
}

const handleSelectFolder = (id: string | null) => {
  selectFolder(id)
  router.push(buildDocumentsLocation(id, documentIdFromRoute.value))
  if (isMobile.value) {
    isSidebarOpen.value = false
  }
}

const {
  documents,
  isLoading: isDocumentsLoading,
  error: documentsError,
  page,
  itemsPerPage,
  totalItems,
  sortBy,
  getSortIcon,
  toggleColumnSort,
  updateFilters,
  reloadDocuments,
  updatePagination,
  deleteDocuments,
  downloadDocuments,
  moveDocuments,
} = useDocuments()

/* «Скрыть недействующие» живёт отдельно от фильтров колонок: сброс фильтров и смена папки его не трогают */
const { hideCompleted: isHideInactiveDocuments } = useTableHideCompleted('documents')

watch(isHideInactiveDocuments, value => {
  updateFilters({ is_actual: value ? true : undefined })
})

const {
  allColumns,
  visibleKeys,
  isVisible,
  save: saveColumnVisibility,
  showFolders,
  setShowFolders,
} = useColumnVisibility()

const isColumnSettingsOpen = ref(false)

const tableDocuments = computed(() =>
  showFolders.value
    ? documents.value
    : documents.value.filter(d => !d.is_dir),
)

const tableItems = computed(() => {
  if (selectedFolderId.value === null) {
    return tableDocuments.value
  }

  const parentRow = {
    id: PARENT_NAV_ID,
    number: '',
    name: '..',
    is_dir: true,
    status: '',
    created_by: { id: '', first_name: '', last_name: '' },
    created_at: '',
    approved_at: null,
    valid_until: null,
    effective_at: null,
  } as DocumentItem

  return [parentRow, ...tableDocuments.value]
})

const getRowProps = ({ item }: { item: DocumentItem }) => {
  if (item.id === PARENT_NAV_ID) {
    return { class: 'parent-nav-row' }
  }

  if (item.is_dir) {
    return { class: 'folder-row' }
  }

  return {}
}

const NON_CONFIGURABLE_KEYS = ['checkbox', 'icon']

const allTableHeaders = [
  {
    key: 'checkbox',
    title: '',
    align: 'start' as const,
    sortable: false,
    width: '56px',
  },
  {
    key: 'icon',
    title: '',
    align: 'start' as const,
    sortable: false,
    width: '56px',
  },
  {
    key: 'number',
    title: 'Номер',
    align: 'start' as const,
    sortable: true,
  },
  {
    key: 'name',
    title: 'Наименование',
    align: 'start' as const,
    sortable: true,
    width: '560px',
    minWidth: '500px',
    maxWidth: '560px',
    cellProps: { class: 'col-name' },
    headerProps: { class: 'col-name' },
  },
  {
    key: 'edition',
    title: 'Редакция документа',
    align: 'start' as const,
    sortable: true,
  },
  {
    key: 'approved_at',
    title: 'Дата утверждения',
    align: 'start' as const,
    sortable: true,
  },
  { key: 'doc_type', title: 'Вид', align: 'start' as const, sortable: false },
  {
    key: 'section',
    title: 'Разделы нормативов',
    align: 'start' as const,
    sortable: false,
  },
  { key: 'status', title: 'Статус', align: 'start' as const, sortable: false },
  { key: 'author', title: 'Разработчик', align: 'start' as const, sortable: false },
  { key: 'created_by', title: 'Разместил документ', align: 'start' as const, sortable: true },
  { key: 'created_at', title: 'Дата загрузки', align: 'start' as const, sortable: true },
  {
    key: 'effective_at',
    title: 'Дата ввода в действие',
    align: 'start' as const,
    sortable: true,
  },
  {
    key: 'valid_until',
    title: 'Дата истечения',
    align: 'start' as const,
    sortable: true,
  },
]

const tableHeaders = computed(() =>
  allTableHeaders.filter(
    h => NON_CONFIGURABLE_KEYS.includes(h.key) || isVisible(h.key),
  ),
)

const onUpdateOptions = (opts: {
  page?: number
  itemsPerPage?: number
  sortBy?: { key: string; order: 'asc' | 'desc' }[]
}) => {
  updatePagination({
    page: opts.page,
    itemsPerPage: opts.itemsPerPage,
    sortBy: opts.sortBy,
  })
  tableWrapperRef.value?.scrollTo(0, 0)
}

const deleteModal = useModal<{ documentIds: string[]; folderIds: string[] }>()
const addModal = useModal()
const editModal = useModal<string>()
const moveModal = useModal<string[]>()

const isDeleteModalOpen = deleteModal.isOpen
const documentsToDelete = computed(() => deleteModal.data.value?.documentIds ?? [])
const foldersToDelete = computed(() => deleteModal.data.value?.folderIds ?? [])
const openDeleteModal = (payload: { documentIds: string[]; folderIds: string[] }) =>
  deleteModal.open(payload)
const closeDeleteModal = () => deleteModal.close()

const isAddModalOpen = addModal.isOpen
const importHistoryOpen = ref(false)

function openDocumentsImport() {
  router.push({
    name: ERouteNames.IMPORT_DATA,
    params: { importType: 'documents' },
    query: { from: route.fullPath, zipOnly: '1' },
  })
}

const openAddModal = () => {
  if (isInGlobalLibrary.value) {
    return
  }

  addModal.open()
}

const isEditModalOpen = editModal.isOpen
const documentToEdit = computed(() => editModal.data.value)
const openEditModal = (id: string) => {
  const documentId = parseDocumentId(id)
  if (documentId == null) {
    return
  }

  editModal.open(documentId)

  const targetFolderId = selectedFolderId.value ?? folderId.value
  if (documentIdFromRoute.value !== documentId) {
    router.replace(buildDocumentsLocation(targetFolderId, documentId))
  }
}

watch(
  documentIdFromRoute,
  id => {
    if (id != null) {
      editModal.open(id)
    } else {
      editModal.close()
    }
  },
  { immediate: true },
)

watch(isEditModalOpen, open => {
  if (!open && documentIdFromRoute.value != null) {
    router.replace(buildDocumentsLocation(selectedFolderId.value ?? folderId.value, null))
  }
})

const documentsToMoveCount = computed(
  () => selectedDocuments.value.length + selectedFolders.value.length,
)

const moveModalTitle = ref('Переместить документы')

const openMoveModal = (ids: string[]) => moveModal.open(ids)
const closeMoveModal = () => moveModal.close()

const MIN_SEARCH_LENGTH = 2
const searchQuery = ref('')

const searchParam = computed(() =>
  searchQuery.value.trim().length >= MIN_SEARCH_LENGTH
    ? searchQuery.value.trim()
    : undefined,
)

const selectedDocuments = ref<string[]>([])
const selectedFolders = ref<string[]>([])
const isDownloading = ref(false)
const isFolderSaving = ref(false)
const isDeleting = ref(false)

/** Управление папками и мутации — только через permissionsStore (CanEditDocument на сервере). */
const canManageFolders = computed(() => canEditDocuments.value)

const canMutateDocumentsHere = computed(() =>
  !isInGlobalLibrary.value || canEditDocuments.value,
)

const foldersWithoutGlobal = computed(() => {
  const globalId = globalFolderId.value
  if (globalId == null) {
    return folders.value
  }

  return folders.value.filter(item => String(item.id) !== String(globalId))
})

const createParentFolder = computed<{ id: FolderItem['id'] | null; name: string } | null>(() => {
  if (!canManageFolders.value || isInGlobalLibrary.value) {
    return null
  }

  const currentId = selectedFolderId.value
  if (!canCreateFolderIn(currentId)) {
    return null
  }

  if (currentId == null || currentId === PARENT_NAV_ID) {
    const fallbackId = localFolderId.value
    const fallbackName = localFolder.value?.name ?? 'Локальные'

    if (fallbackId == null) {
      return null
    }

    return {
      id: fallbackId,
      name: fallbackName,
    }
  }

  return {
    id: currentId,
    name: selectedFolder.value?.name ?? '',
  }
})

const renameTargetFolder = computed<FolderItem | null>(() => {
  if (!canManageFolders.value || isInGlobalLibrary.value) {
    return null
  }

  if (selectedFolders.value.length === 1 && selectedDocuments.value.length === 0) {
    const selectedId = selectedFolders.value[0]
    if (selectedId != null && canEditFolder(selectedId)) {
      return findFolder(selectedId) ?? {
        id: selectedId,
        name: tableDocuments.value.find(item => item.is_dir && item.id === selectedId)?.name ?? '',
        count_documents: 0,
        children: [],
      }
    }

    return null
  }

  if (selectedFolderId.value != null && canEditFolder(selectedFolderId.value)) {
    return selectedFolder.value
  }

  return null
})

const isFolderNameModalOpen = ref(false)
const folderNameMode = ref<'create' | 'rename'>('create')
const folderNameInitial = ref('')
const folderNameParent = ref('')
const folderCreateParentId = ref<FolderItem['id'] | null>(null)
const folderRenameId = ref<FolderItem['id'] | null>(null)

const toFolderIdOrNull = (id: FolderItem['id'] | null | undefined): FolderItem['id'] | null => {
  if (id == null || id === PARENT_NAV_ID) {
    return null
  }

  return id
}

const moveTargetFolderIds = computed(() => {
  if (!canManageFolders.value || isInGlobalLibrary.value) {
    return [] as FolderItem['id'][]
  }

  const selectedIds = selectedFolders.value
    .map(id => toFolderIdOrNull(id))
    .filter((id): id is FolderItem['id'] => id != null && canEditFolder(id))

  if (selectedIds.length > 0) {
    return selectedIds
  }

  const currentId = toFolderIdOrNull(selectedFolderId.value)
  if (currentId != null && canEditFolder(currentId)) {
    return [currentId]
  }

  return []
})

const openCreateFolderModal = (
  targetParentId: FolderItem['id'] | null = createParentFolder.value?.id ?? null,
  parentFolderName = createParentFolder.value?.name ?? '',
) => {
  if (!canManageFolders.value || isInGlobalLibrary.value) {
    return
  }

  const resolvedParentId = targetParentId == null ? localFolderId.value : targetParentId
  if (resolvedParentId == null || !canCreateFolderIn(resolvedParentId)) {
    return
  }

  const parent = findFolder(resolvedParentId)
  folderNameMode.value = 'create'
  folderNameInitial.value = ''
  folderNameParent.value = (targetParentId == null ? localFolder.value?.name : parentFolderName)
    || parent?.name
    || localFolder.value?.name
    || ''
  folderCreateParentId.value = resolvedParentId
  folderRenameId.value = null
  isFolderNameModalOpen.value = true
}

const openRenameFolderModal = (
  targetFolderId: FolderItem['id'] | null = renameTargetFolder.value?.id ?? null,
  targetFolderName = renameTargetFolder.value?.name ?? '',
) => {
  if (
    !canManageFolders.value
    || isInGlobalLibrary.value
    || targetFolderId == null
    || isFolderInGlobalLibrary(targetFolderId)
    || !canEditFolder(targetFolderId)
  ) {
    return
  }

  folderNameMode.value = 'rename'
  folderNameInitial.value = targetFolderName || findFolder(targetFolderId)?.name || ''
  folderNameParent.value = ''
  folderCreateParentId.value = null
  folderRenameId.value = targetFolderId
  isFolderNameModalOpen.value = true
}

const handleFolderNameSubmit = async (name: string) => {
  if (isFolderSaving.value) {
    return
  }

  isFolderSaving.value = true
  try {
    if (folderNameMode.value === 'create') {
      const rawParentId = folderCreateParentId.value
      const isLibraryRoot = rawParentId != null && isLibraryRootId(rawParentId)
      const parentId = isLibraryRoot ? null : toFolderIdOrNull(rawParentId)
      if (!isLibraryRoot && parentId == null) {
        throw new Error('Не выбрана родительская папка')
      }

      await FoldersApi.createFolder({ name, parent_id: parentId })
      showSuccess('Папка создана')
      isFolderNameModalOpen.value = false
      await loadFolders()

      if (parentId != null && String(parentId) !== String(selectedFolderId.value)) {
        handleSelectFolder(parentId)
      } else {
        await applyAllFilters()
      }
    } else if (folderRenameId.value != null) {
      await FoldersApi.updateFolder(folderRenameId.value, { name })
      showSuccess('Папка переименована')
      isFolderNameModalOpen.value = false
      await loadFolders()
      await applyAllFilters()
    }
  } catch (err) {
    console.error('Ошибка сохранения папки:', err)
    showError(err, folderNameMode.value === 'create' ? 'Создание папки' : 'Переименование папки')
  } finally {
    isFolderSaving.value = false
  }
}

const selectedEditableFolders = computed(() =>
  selectedFolders.value.filter(id => canEditFolder(id)),
)

const hasDeletableSelection = computed(() =>
  (canMutateDocumentsHere.value && selectedDocuments.value.length > 0)
  || selectedEditableFolders.value.length > 0,
)

const onShowFoldersChange = (value: boolean) => {
  setShowFolders(value)
  if (!value) {
    selectedFolders.value = []
  }
}

const onColumnSettingsSave = (payload: { keys: string[]; showFolders: boolean }) => {
  saveColumnVisibility(payload.keys)
  onShowFoldersChange(payload.showFolders)
}

const docsFilter = useDocumentsFilterStore()
const filterMenus = ref<Record<string, boolean>>({})
const tempSelectedFilters = ref<Record<string, string[]>>({})

watch(
  isInGlobalLibrary,
  isGlobal => {
    docsFilter.setFilterSource(isGlobal ? 'global' : 'local')
  },
  { immediate: true },
)

const {
  activeFilters,
  isNumberFilterActive,
  isDateCreatedFilterActive,
  isDateApprovedFilterActive,
  isDateValidUntilFilterActive,
  applyNumberFilter,
  applyDateCreatedFilter,
  applyDateApprovedFilter,
  applyDateValidUntilFilter,
  resetAllFilters,
} = useFilters()

function findLabelRecursive(
  nodes: DocumentTreeItem[] | undefined,
  id: string | number,
): string | undefined {
  if (!nodes?.length) {
    return undefined
  }
  for (const n of nodes) {
    if (String(n.id) === String(id)) {
      return n.label
    }
    const found = findLabelRecursive(n.children, id)
    if (found) {
      return found
    }
  }

  return undefined
}

function getLabel(field: string, id: string | number): string {
  const label
    = findLabelRecursive(docsFilter.searchChoices[field], id)
    || findLabelRecursive(docsFilter.filterChoices[field], id)

  return label || String(id)
}

/** Для папок в таблице — пробел вместо прочерка */
const displayCell = (item: { is_dir?: boolean }, value?: string | null) =>
  item.is_dir ? ' ' : (value || '—')

function formatDocumentPersonShort(person?: {
  last_name?: string
  first_name?: string
  middle_name?: string
} | null): string {
  if (!person) {
    return '—'
  }

  return `${person.last_name} ${person.first_name?.[0] ?? ''}.${person.middle_name?.[0] ? `${person.middle_name[0]}.` : ''}`
}

function formatDocumentAuthors(item: {
  author?: {
    last_name?: string
    first_name?: string
    middle_name?: string
  } | null
  authors?: Array<{
    last_name?: string
    first_name?: string
    middle_name?: string
  }>
}): string {
  if (item.authors && item.authors.length > 0) {
    const formatted = item.authors
      .map(formatDocumentPersonShort)
      .filter(name => name && name !== '—')
      .join(', ')

    return formatted || '—'
  }

  return formatDocumentPersonShort(item.author)
}

function normalizeNumbersFilter(value: string | null | undefined): string | undefined {
  if (!value?.trim()) {
    return undefined
  }

  const parts = value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)

  return parts.length ? parts.join(',') : undefined
}

const activeFilterChips = computed<FilterChip[]>(() => {
  const chips: FilterChip[] = []
  const f = docsFilter.filters
  if (f.types?.length) {
    f.types.forEach(id =>
      chips.push({
        label: `Вид: ${getLabel('types', id)}`,
        type: 'types',
        value: id,
      }),
    )
  }
  if (f.status?.length) {
    f.status.forEach(id =>
      chips.push({
        label: `Статус: ${getLabel('status', id)}`,
        type: 'status',
        value: id,
      }),
    )
  }
  if (f.sections?.length) {
    f.sections.forEach(id =>
      chips.push({
        label: `Разделы нормативов: ${getLabel('sections', id)}`,
        type: 'sections',
        value: id,
      }),
    )
  }
  if (f.authors?.length) {
    f.authors.forEach(id =>
      chips.push({
        label: `Разработчик: ${getLabel('authors', id)}`,
        type: 'authors',
        value: id,
      }),
    )
  }
  if (activeFilters.value.number) {
    chips.push({
      label: `Номер: ${activeFilters.value.number}`,
      type: 'number',
    })
  }
  if (
    activeFilters.value.dateCreatedFrom
    || activeFilters.value.dateCreatedTo
  ) {
    const d = activeFilters.value
    let lab = 'Дата создания: '
    if (d.dateCreatedFrom && d.dateCreatedTo) {
      lab += `${formatDateOnly(d.dateCreatedFrom)} - ${formatDateOnly(d.dateCreatedTo)}`
    } else if (d.dateCreatedFrom) {
      lab += `с ${formatDateOnly(d.dateCreatedFrom)}`
    } else if (d.dateCreatedTo) {
      lab += `по ${formatDateOnly(d.dateCreatedTo)}`
    }
    chips.push({ label: lab, type: 'dateCreated' })
  }
  if (
    activeFilters.value.dateApprovedFrom
    || activeFilters.value.dateApprovedTo
  ) {
    const d = activeFilters.value
    let lab = 'Дата утверждения: '
    if (d.dateApprovedFrom && d.dateApprovedTo) {
      lab += `${formatDateOnly(d.dateApprovedFrom)} - ${formatDateOnly(d.dateApprovedTo)}`
    } else if (d.dateApprovedFrom) {
      lab += `с ${formatDateOnly(d.dateApprovedFrom)}`
    } else if (d.dateApprovedTo) {
      lab += `по ${formatDateOnly(d.dateApprovedTo)}`
    }
    chips.push({ label: lab, type: 'dateApproved' })
  }
  if (
    activeFilters.value.dateValidUntilFrom
    || activeFilters.value.dateValidUntilTo
  ) {
    const d = activeFilters.value
    let lab = 'Дата истечения: '
    if (d.dateValidUntilFrom && d.dateValidUntilTo) {
      lab += `${formatDateOnly(d.dateValidUntilFrom)} - ${formatDateOnly(d.dateValidUntilTo)}`
    } else if (d.dateValidUntilFrom) {
      lab += `с ${formatDateOnly(d.dateValidUntilFrom)}`
    } else if (d.dateValidUntilTo) {
      lab += `по ${formatDateOnly(d.dateValidUntilTo)}`
    }
    chips.push({ label: lab, type: 'dateValidUntil' })
  }
  if (searchParam.value) {
    chips.push({ label: `Поиск: ${searchParam.value}`, type: 'search' })
  }

  return chips
})

const getFilterIcon = (isActive: boolean) =>
  isActive ? 'ri-filter-fill' : 'ri-filter-2-line'

function getDocumentStatusString(item: DocumentItem): string | undefined {
  if (item.is_dir) {
    return undefined
  }
  const s: unknown = item.status
  if (typeof s === 'string' && s) {
    return s
  }
  if (s && typeof s === 'object' && 'slug' in s) {
    return (s as { slug: string }).slug
  }
  if (s && typeof s === 'object' && 'name' in s) {
    return (s as { name: string }).name
  }
  const statusName = item.status_name
  if (typeof statusName === 'string' && statusName) {
    return statusName
  }

  return undefined
}

function getDocumentStatusDisplay(item: DocumentItem) {
  const str = getDocumentStatusString(item)
  if (!str) {
    return null
  }
  const config = getDocumentStatusConfig(str)

  return {
    label: getDocumentStatusLabel(str),
    bgColor: config.bgColor,
    textColor: config.textColor,
  }
}

const moveModalRef = ref<InstanceType<typeof DocumentMoveModal>>()
const tableWrapperRef = ref<HTMLElement | null>(null)

const toggleDocumentSelection = (item: DocumentItem) => {
  if (item.is_dir) {
    const index = selectedFolders.value.indexOf(item.id)
    if (index > -1) {
      selectedFolders.value.splice(index, 1)
    } else selectedFolders.value.push(item.id)
  } else {
    const index = selectedDocuments.value.indexOf(item.id)
    if (index > -1) {
      selectedDocuments.value.splice(index, 1)
    } else selectedDocuments.value.push(item.id)
  }
}

const toggleAllDocuments = () => {
  const allSelected
    = selectedDocuments.value.length + selectedFolders.value.length
    === tableDocuments.value.length

  if (allSelected) {
    selectedDocuments.value = []
    selectedFolders.value = []
  } else {
    selectedDocuments.value = tableDocuments.value
      .filter(d => !d.is_dir)
      .map(d => d.id)
    selectedFolders.value = tableDocuments.value
      .filter(d => d.is_dir)
      .map(d => d.id)
  }
}

const allSelected = computed(() => {
  return (
    tableDocuments.value.length > 0
    && selectedDocuments.value.length + selectedFolders.value.length
      === tableDocuments.value.length
  )
})

const isItemSelected = (itemId: string) => {
  return (
    selectedDocuments.value.includes(itemId)
    || selectedFolders.value.includes(itemId)
  )
}

const handleDownload = async () => {
  if (isDownloading.value) {
    return
  }
  if (!selectedDocuments.value.length && !selectedFolders.value.length) {
    return
  }

  const documentIds = [...selectedDocuments.value]
  const folderIds = [...selectedFolders.value]
  const totalCount = documentIds.length + folderIds.length

  isDownloading.value = true
  try {
    await downloadDocuments(documentIds, folderIds)

    if (totalCount === 1) {
      showSuccess('Успешно скачано')
    } else {
      showSuccess(
        `${totalCount} ${pluralize(totalCount, 'элемент', 'элемента', 'элементов')} скачано в архиве`,
      )
    }

    selectedDocuments.value = []
    selectedFolders.value = []
  } catch (err) {
    console.error('Ошибка скачивания:', err)
    showError(err, 'Скачивание')
  } finally {
    isDownloading.value = false
  }
}

const handleDelete = () => {
  const documentIds = canMutateDocumentsHere.value ? [...selectedDocuments.value] : []
  const folderIds = selectedFolders.value.filter(id => canEditFolder(id))
  if (!documentIds.length && !folderIds.length) {
    return
  }

  openDeleteModal({ documentIds, folderIds })
}

const confirmDelete = async () => {
  const documentIds = documentsToDelete.value
  const folderIds = foldersToDelete.value
  if (!documentIds.length && !folderIds.length) {
    return
  }

  const leaveFolderId = parentFolderId.value ?? localFolderId.value ?? null
  const shouldLeaveCurrentFolder = folderIds.some(
    id => selectedFolderId.value != null && String(id) === String(selectedFolderId.value),
  )

  isDeleting.value = true
  try {
    if (documentIds.length) {
      await deleteDocuments(documentIds)
    }
    for (const id of folderIds) {
      await FoldersApi.deleteFolder(id)
    }

    selectedDocuments.value = []
    selectedFolders.value = []
    closeDeleteModal()

    await loadFolders()

    if (shouldLeaveCurrentFolder || (selectedFolderId.value != null && !findFolder(selectedFolderId.value))) {
      handleSelectFolder(leaveFolderId)
    } else {
      await applyAllFilters()
    }

    const totalCount = documentIds.length + folderIds.length
    showSuccess(
      totalCount === 1
        ? 'Элемент удалён'
        : `${totalCount} ${pluralize(totalCount, 'элемент', 'элемента', 'элементов')} удалено`,
    )
  } catch (err) {
    console.error('Ошибка удаления:', err)
    showError(err, 'Удаление')
  } finally {
    isDeleting.value = false
  }
}

const getDeleteMessage = computed(() => {
  const documentCount = documentsToDelete.value.length
  const folderCount = foldersToDelete.value.length
  const totalCount = documentCount + folderCount

  if (folderCount === 1 && documentCount === 0) {
    const folder = findFolder(foldersToDelete.value[0])
    const folderName = folder?.name ? ` «${folder.name}»` : ''

    return {
      title: `Вы уверены, что хотите удалить папку${folderName}?`,
      text: 'Будут удалены все вложенные папки и документы. Их невозможно будет восстановить.',
    }
  }

  if (folderCount > 0 && documentCount === 0) {
    return {
      title: 'Вы уверены, что хотите удалить эти папки?',
      text: `Будет удалено ${folderCount} ${pluralize(folderCount, 'папка', 'папки', 'папок')} вместе с содержимым. Их невозможно будет восстановить.`,
    }
  }

  if (totalCount === 1) {
    return {
      title: 'Вы уверены, что хотите удалить этот элемент?',
      text: 'Его невозможно будет восстановить.',
    }
  }

  const word = pluralize(totalCount, 'элемент', 'элемента', 'элементов')
  const folderNote = folderCount > 0
    ? ' Папки будут удалены вместе с содержимым.'
    : ''

  return {
    title: 'Вы уверены, что хотите удалить эти элементы?',
    text: `Будет удалено ${totalCount} ${word}.${folderNote} Их невозможно будет восстановить.`,
  }
})

const handleDocumentCreated = async (targetFolderId: string | null) => {
  const normalizedFolderId
    = targetFolderId
      ? targetFolderId
      : localFolderId.value

  await loadFolders()

  if (normalizedFolderId !== selectedFolderId.value) {
    handleSelectFolder(normalizedFolderId)
  } else {
    await applyAllFilters()
  }
}

const handleDocumentUpdated = async () => {
  await reloadDocuments()
  await loadFolders()
}

const handleDocumentDeleted = async () => {
  await reloadDocuments()
  await loadFolders()
}

const handleIconClick = (doc: DocumentItem, event: MouseEvent) => {
  event.stopPropagation()
  event.preventDefault()

  if (doc.id === PARENT_NAV_ID) {
    handleSelectFolder(parentFolderId.value ?? null)

    return
  }

  if (doc.is_dir) {
    handleSelectFolder(doc.id)

    return
  }

  openEditModal(doc.id)
}

const onTableRowClick = (event: MouseEvent, { item }: { item: DocumentItem }) => {
  handleRowClick(item, (event as MouseEvent & { originalEvent?: MouseEvent }).originalEvent || event)
}

const handleRowClick = async (doc: DocumentItem, event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.closest('.col-checkbox') || target.closest('.col-icon')) {
    return
  }

  if (doc.id === PARENT_NAV_ID) {
    handleSelectFolder(parentFolderId.value ?? null)

    return
  }

  if (doc.is_dir) {
    handleSelectFolder(doc.id)

    return
  }

  if (doc.download_url) {
    window.open(doc.download_url, '_blank')

    return
  }
  try {
    const { data: blob } = await DocumentsApi.downloadDocuments([doc.id], [])
    const url = URL.createObjectURL(blob)

    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  } catch (err) {
    console.error('Ошибка открытия документа:', err)
    showError(err, 'Открытие документа')
  }
}

const handleMove = () => {
  if (isInGlobalLibrary.value) {
    return
  }

  const documentIds = canMutateDocumentsHere.value ? [...selectedDocuments.value] : []
  if (!documentIds.length) {
    return
  }

  selectedFolders.value = []
  moveModalTitle.value = 'Переместить документы'
  openMoveModal(documentIds)
  moveModalRef.value?.open()
}

const handleMoveFolder = () => {
  if (isInGlobalLibrary.value) {
    return
  }

  const folderIds = [...moveTargetFolderIds.value]
  if (!folderIds.length) {
    return
  }

  selectedDocuments.value = []
  selectedFolders.value = folderIds
  moveModalTitle.value = folderIds.length === 1 ? 'Переместить папку' : 'Переместить папки'
  openMoveModal(folderIds)
  moveModalRef.value?.open()
}

const confirmMove = async (folderId: FolderItem['id']) => {
  if (isFolderInGlobalLibrary(folderId)) {
    return
  }

  if (!selectedDocuments.value.length && !selectedFolders.value.length) {
    return
  }

  try {
    await moveDocuments(
      selectedDocuments.value,
      selectedFolders.value,
      folderId,
    )

    const count = selectedDocuments.value.length + selectedFolders.value.length
    const word = pluralize(count, 'элемент', 'элемента', 'элементов')

    showSuccess(`${count} ${word} успешно перемещено`)
    selectedDocuments.value = []
    selectedFolders.value = []
    closeMoveModal()

    await loadFolders()
    handleSelectFolder(folderId)
  } catch (err) {
    console.error('Ошибка переноса:', err)
    showError(err, 'Перенос')
  }
}

const hasActiveFilters = computed(() => {
  return (
    !!searchParam.value
    || isNumberFilterActive.value
    || (docsFilter.filters.types?.length ?? 0) > 0
    || (docsFilter.filters.status?.length ?? 0) > 0
    || (docsFilter.filters.sections?.length ?? 0) > 0
    || (docsFilter.filters.authors?.length ?? 0) > 0
    || isDateCreatedFilterActive.value
    || isDateApprovedFilterActive.value
    || isDateValidUntilFilterActive.value
  )
})

const resetFilters = async () => {
  searchQuery.value = ''
  selectedDocuments.value = []
  selectedFolders.value = []
  resetAllFilters()
  docsFilter.resetTreeFilters()

  const defaultFolderId = localFolderId.value
  selectFolder(defaultFolderId)
  await router.replace(
    defaultFolderId != null
      ? buildDocumentsLocation(defaultFolderId, null)
      : buildDocumentsLocation(null, null),
  )

  await updateFilters({
    search: undefined,
    folder: defaultFolderId ?? undefined,
    source: 'local',
    numbers: undefined,
    types: undefined,
    status: undefined,
    sections: undefined,
    authors: undefined,
    created_after: undefined,
    created_before: undefined,
    approved_after: undefined,
    approved_before: undefined,
    valid_until_after: undefined,
    valid_until_before: undefined,
    sort: undefined,
  })
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) {
    return '—'
  }

  return new Date(dateString).toLocaleDateString('ru-RU')
}

watch(selectedFolderId, () => applyAllFilters())

let searchTimeout: ReturnType<typeof setTimeout> | null = null
watch(searchQuery, () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  const len = searchQuery.value.trim().length
  if (len > 0 && len < MIN_SEARCH_LENGTH) {
    return
  }
  searchTimeout = setTimeout(() => applyAllFilters(), 500)
})

async function applyAllFilters() {
  const f = docsFilter.filters

  await updateFilters({
    search: searchParam.value ?? undefined,
    folder: selectedFolderId.value || undefined,
    source: docsFilter.source,
    numbers: normalizeNumbersFilter(activeFilters.value.number),
    types: (f.types?.length ?? 0) > 0 ? f.types!.map(String) : undefined,
    status: (f.status?.length ?? 0) > 0 ? f.status.map(String) : undefined,
    sections: (f.sections?.length ?? 0) > 0 ? f.sections.map(String) : undefined,
    authors: (f.authors?.length ?? 0) > 0 ? f.authors.map(String) : undefined,
    created_after: activeFilters.value.dateCreatedFrom || undefined,
    created_before: activeFilters.value.dateCreatedTo || undefined,
    approved_after: activeFilters.value.dateApprovedFrom || undefined,
    approved_before: activeFilters.value.dateApprovedTo || undefined,
    valid_until_after: activeFilters.value.dateValidUntilFrom || undefined,
    valid_until_before: activeFilters.value.dateValidUntilTo || undefined,
    is_actual: isHideInactiveDocuments.value ? true : undefined,
  })
}

const handleFilterClick = async (filterKey: string) => {
  tempSelectedFilters.value[filterKey] = [
    ...(docsFilter.filters[filterKey as 'types' | 'status' | 'sections' | 'authors'] || []).map(String),
  ]
  await docsFilter.fetchFilterChoices(filterKey)
  filterMenus.value[filterKey] = true
}

const applyFilter = async (filterKey: string) => {
  (docsFilter.filters as Record<string, string[]>)[filterKey] = [
    ...(tempSelectedFilters.value[filterKey] || []).map(String),
  ]
  filterMenus.value[filterKey] = false
  await applyAllFilters()
}

const handleNumberFilterApply = async (value: string | null) => {
  applyNumberFilter(value)
  await applyAllFilters()
}

const handleDateApprovedFilterApply = async (
  from: string | null,
  to: string | null,
) => {
  applyDateApprovedFilter(from, to)
  await applyAllFilters()
}

const handleDateValidUntilFilterApply = async (
  from: string | null,
  to: string | null,
) => {
  applyDateValidUntilFilter(from, to)
  await applyAllFilters()
}

const removeFilter = async (chip: FilterChip) => {
  switch (chip.type) {
    case 'types':
      docsFilter.filters.types = docsFilter.filters.types.filter(
        id => id !== chip.value,
      )
      break
    case 'status':
      docsFilter.filters.status = docsFilter.filters.status.filter(
        id => id !== chip.value,
      )
      break
    case 'sections':
      docsFilter.filters.sections = docsFilter.filters.sections.filter(
        id => id !== chip.value,
      )
      break
    case 'authors':
      docsFilter.filters.authors = docsFilter.filters.authors.filter(
        id => id !== chip.value,
      )
      break
    case 'number':
      applyNumberFilter(null)
      break
    case 'dateCreated':
      applyDateCreatedFilter(null, null)
      break
    case 'dateApproved':
      applyDateApprovedFilter(null, null)
      break
    case 'dateValidUntil':
      applyDateValidUntilFilter(null, null)
      break
    case 'search':
      searchQuery.value = ''
      break
  }
  await applyAllFilters()
}

watch(
  isMobile,
  mobile => {
    isSidebarOpen.value = !mobile
  },
  { immediate: true },
)

onMounted(async () => {
  await loadFolders()

  if (folderId.value == null && localFolderId.value != null) {
    selectFolder(localFolderId.value)
    await router.replace(
      buildDocumentsLocation(localFolderId.value, documentIdFromRoute.value),
    )
  }

  await applyAllFilters()
})
</script>

<template>
  <div class="documents-new-container">
    <div
      v-if="isMobile && isSidebarOpen"
      class="sidebar-overlay"
      @click="isSidebarOpen = false"
    />

    <aside
      v-if="isSidebarOpen"
      class="sidebar"
      :class="{ 'sidebar-mobile': isMobile }"
      :style="isMobile ? undefined : { width: `${sidebarPaneWidth}px` }"
    >
      <div
        v-if="isMobile"
        class="sidebar-mobile-header"
      >
        <span class="sidebar-title">Папки</span>
        <VIconBtn
          variant="text"
          icon="ri-close-line"
          size="small"
          @click="isSidebarOpen = false"
        />
      </div>

      <div
        v-if="isFoldersLoading && folders.length === 0"
        class="sidebar-state"
      >
        <VProgressCircular
          indeterminate
          color="primary"
          size="32"
        />
        <span>Загрузка папок...</span>
      </div>

      <div
        v-else-if="foldersError"
        class="sidebar-state"
      >
        <VIcon
          icon="ri-error-warning-line"
          color="error"
          size="32"
        />
        <span>{{ foldersError }}</span>
      </div>

      <div
        v-else
        class="folders-tree-wrapper"
      >
        <FolderTree
          :folders="folders"
          :root-folder="rootFolder"
          :selected-folder-id="selectedFolderId"
          :expand-path-to="selectedFolderId"
          :library-root-id="libraryRootId"
          :expand-local="!isInGlobalLibrary"
          :expand-global="isInGlobalLibrary"
          selectable
          @select="handleSelectFolder"
          @toggle="toggleFolder"
          @expand-root="expandRootFolder"
          @toggle-root="toggleRootFolder"
        />
      </div>
    </aside>

    <div
      v-if="isSidebarOpen && !isMobile"
      class="entity-form-resizer"
      :class="{ 'is-resizing': isSidebarResizing }"
      @mousedown="startSidebarResize"
    />

    <main class="main-content">
      <div class="toolbar-header">
        <VIconBtn
          v-if="isMobile"
          variant="text"
          icon="ri-folder-open-line"
          class="menu-toggle-btn"
          @click="isSidebarOpen = !isSidebarOpen"
        />

        <div class="d-flex align-center ga-3 toolbar-header-actions">
          <VTextField
            v-model="searchQuery"
            density="compact"
            placeholder="Найти..."
            variant="solo"
            flat
            hide-details
            prepend-inner-icon="ri-search-line"
            class="search-field"
          />

          <template v-if="canMutateDocumentsHere && canEditDocuments && !isInGlobalLibrary">
            <VBtn
              color="primary"
              size="default"
              class="add-button"
              prepend-icon="ri-add-line"
              @click="openAddModal"
            >
              <span v-if="!isMobile">Добавить документ</span>
              <span v-else>Добавить</span>
            </VBtn>
            <VMenu
              location="bottom end"
              content-class="entity-form-menu"
            >
              <template #activator="{ props: menuProps }">
                <VBtn
                  v-bind="menuProps"
                  variant="outlined"
                  color="secondary"
                  size="default"
                  class="add-button"
                  prepend-icon="ri-download-2-line"
                  append-icon="ri-arrow-down-s-line"
                >
                  <span v-if="!isMobile">Импорт документов</span>
                  <span v-else>Импорт</span>
                </VBtn>
              </template>
              <VList density="compact" min-width="220">
                <VListItem
                  title="Начать импорт"
                  @click="openDocumentsImport"
                />
                <VListItem
                  title="История импорта"
                  @click="importHistoryOpen = true"
                />
              </VList>
            </VMenu>
          </template>
        </div>
      </div>

      <div class="toolbar-actions">
        <div class="actions-left">
          <VTooltip text="Скачать">
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="d-inline-flex action-btn-wrap"
              >
                <VIconBtn
                  variant="outlined"
                  color="secondary"
                  class="action-icon-btn"
                  icon="ri-download-line"
                  :disabled="
                    isDownloading
                      || selectedDocuments.length === 0
                      || selectedFolders.length > 0
                  "
                  :loading="isDownloading"
                  size="small"
                  @click="handleDownload"
                />
              </span>
            </template>
          </VTooltip>
          <VTooltip v-if="!isInGlobalLibrary" text="Создать папку">
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="d-inline-flex action-btn-wrap"
              >
                <VIconBtn
                  variant="outlined"
                  color="secondary"
                  class="action-icon-btn"
                  icon="ri-folder-add-line"
                  :disabled="!createParentFolder"
                  size="small"
                  @click="() => openCreateFolderModal()"
                />
              </span>
            </template>
          </VTooltip>
          <VTooltip v-if="!isInGlobalLibrary" text="Переименовать папку">
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="d-inline-flex action-btn-wrap"
              >
                <VIconBtn
                  variant="outlined"
                  color="secondary"
                  class="action-icon-btn"
                  icon="ri-pencil-line"
                  :disabled="!renameTargetFolder"
                  size="small"
                  @click="() => openRenameFolderModal()"
                />
              </span>
            </template>
          </VTooltip>
          <VTooltip v-if="!isInGlobalLibrary" text="Переместить папку">
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="d-inline-flex action-btn-wrap"
              >
                <VIconBtn
                  variant="outlined"
                  color="secondary"
                  class="action-icon-btn"
                  icon="ri-folder-transfer-line"
                  :disabled="moveTargetFolderIds.length === 0"
                  size="small"
                  @click="handleMoveFolder"
                />
              </span>
            </template>
          </VTooltip>
          <VTooltip text="Удалить">
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="d-inline-flex action-btn-wrap"
              >
                <VIconBtn
                  variant="outlined"
                  color="secondary"
                  class="action-icon-btn"
                  icon="ri-delete-bin-line"
                  :disabled="!hasDeletableSelection"
                  size="small"
                  @click="handleDelete"
                />
              </span>
            </template>
          </VTooltip>
          <VTooltip v-if="!isInGlobalLibrary" text="Переместить документы">
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="d-inline-flex action-btn-wrap"
              >
                <VIconBtn
                  variant="outlined"
                  color="secondary"
                  class="action-icon-btn"
                  icon="ri-external-link-line"
                  :disabled="!canMutateDocumentsHere || selectedDocuments.length === 0"
                  size="small"
                  @click="handleMove"
                />
              </span>
            </template>
          </VTooltip>
        </div>

        <div class="actions-right">
          <VBtn
            v-if="canEditDocuments"
            prepend-icon="ri-file-list-3-line"
            variant="outlined"
            color="secondary"
            class="text-color-secondary"
            size="small"
            @click="isDocumentTypeModalOpen = true"
          >
            <span v-if="!isSmallScreen">Виды документов</span>
            <span v-else>Виды</span>
          </VBtn>

          <VBtn
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            class="text-color-secondary"
            :disabled="!hasActiveFilters"
            size="small"
            @click="resetFilters"
          >
            <span v-if="!isSmallScreen">Сбросить фильтры</span>
            <span v-else>Сбросить</span>
          </VBtn>

          <VSwitch
            v-model="isHideInactiveDocuments"
            label="Скрыть недействующие"
            density="compact"
            color="primary"
            hide-details
            class="flex-grow-0"
          />

          <VMenu location="bottom end">
            <template #activator="{ props: menuProps }">
              <VIcon
                v-bind="menuProps"
                icon="ri-more-2-fill"
                size="20"
                class="menu-icon"
              />
            </template>
            <VList density="compact" min-width="260">
              <VListItem
                title="Настройка списка документов"
                prepend-icon="ri-settings-3-line"
                @click="isColumnSettingsOpen = true"
              />
            </VList>
          </VMenu>
        </div>
      </div>

      <div
        v-if="activeFilterChips.length > 0"
        class="filter-chips-container"
      >
        <VChip
          v-for="chip in activeFilterChips"
          :key="`${chip.type}-${chip.value ?? 'n'}`"
          closable
          size="small"
          class="filter-chip"
          @click:close="removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <div
        ref="tableWrapperRef"
        class="documents-table-wrapper"
      >
        <VDataTableServer
          :items-per-page="itemsPerPage"
          :page="page"
          :sort-by="sortBy"
          :loading="isDocumentsLoading"
          :headers="tableHeaders"
          :items="tableItems"
          :items-length="totalItems || documents.length"
          item-value="id"
          class="documents-table no-row-gaps cursor-pointer-table custom-data-table"
          :class="{ 'dark-theme': theme.global.current.value.dark }"
          :row-props="getRowProps"
          @update:options="onUpdateOptions"
          @click:row="onTableRowClick"
        >
          <template #header.checkbox>
            <VCheckbox
              :model-value="allSelected"
              hide-details
              density="compact"
              class="col-checkbox"
              @update:model-value="toggleAllDocuments"
            />
          </template>
          <template #header.icon>
            <span class="col-icon" />
          </template>
          <template #header.number>
            <span class="th-content d-flex align-center">
              <span
                class="sortable-title d-flex align-center"
                @click="toggleColumnSort('number')"
              >
                <span>Номер</span>
                <VIcon
                  :icon="getSortIcon('number')"
                  size="16"
                  class="ml-1"
                />
              </span>
              <NumberFilterDropdown
                :value="activeFilters.number"
                :active="isNumberFilterActive"
                @apply="handleNumberFilterApply"
              />
            </span>
          </template>
          <template #header.doc_type>
            <span class="th-content d-flex align-center">
              <span>Вид</span>
              <div
                v-if="docsFilter.filterLoading.types"
                class="d-flex align-center justify-center ml-1"
                style="width: 28px; height: 28px"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="20"
                  width="2"
                />
              </div>
              <VMenu
                v-else
                v-model="filterMenus.types"
                :close-on-content-click="false"
                location="bottom start"
                transition="scale-transition"
              >
                <template #activator="{ props: menuProps }">
                  <VIconBtn
                    v-bind="menuProps"
                    variant="plain"
                    size="small"
                    class="filter-icon-btn ml-1"
                    :icon="
                      getFilterIcon((docsFilter.filters.types?.length ?? 0) > 0)
                    "
                    @click.stop="handleFilterClick('types')"
                  />
                </template>
                <UniversalFilterTree
                  field-key="types"
                  module="document"
                  item-children-key="children"
                  :store="docsFilter"
                  :model-value="tempSelectedFilters.types || []"
                  placeholder="Поиск: Вид"
                  @update:model-value="tempSelectedFilters.types = $event"
                  @apply="applyFilter('types')"
                />
              </VMenu>
            </span>
          </template>
          <template #header.status>
            <span class="th-content d-flex align-center">
              <span>Статус</span>
              <div
                v-if="docsFilter.filterLoading.status"
                class="d-flex align-center justify-center ml-1"
                style="width: 28px; height: 28px"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="20"
                  width="2"
                />
              </div>
              <VMenu
                v-else
                v-model="filterMenus.status"
                :close-on-content-click="false"
                location="bottom start"
                transition="scale-transition"
              >
                <template #activator="{ props: menuProps }">
                  <VIconBtn
                    v-bind="menuProps"
                    variant="plain"
                    size="small"
                    class="filter-icon-btn ml-1"
                    :icon="
                      getFilterIcon(
                        (docsFilter.filters.status?.length ?? 0) > 0,
                      )
                    "
                    @click.stop="handleFilterClick('status')"
                  />
                </template>
                <UniversalFilterTree
                  field-key="status"
                  module="document"
                  item-children-key="children"
                  :store="docsFilter"
                  :model-value="tempSelectedFilters.status || []"
                  placeholder="Поиск: Статус"
                  @update:model-value="tempSelectedFilters.status = $event"
                  @apply="applyFilter('status')"
                />
              </VMenu>
            </span>
          </template>
          <template #header.author>
            <span class="th-content d-flex align-center">
              <span>Разработчик</span>
              <div
                v-if="docsFilter.filterLoading.authors"
                class="d-flex align-center justify-center ml-1"
                style="width: 28px; height: 28px"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="20"
                  width="2"
                />
              </div>
              <VMenu
                v-else
                v-model="filterMenus.authors"
                :close-on-content-click="false"
                location="bottom start"
                transition="scale-transition"
              >
                <template #activator="{ props: menuProps }">
                  <VIconBtn
                    v-bind="menuProps"
                    variant="plain"
                    size="small"
                    class="filter-icon-btn ml-1"
                    :icon="
                      getFilterIcon(
                        (docsFilter.filters.authors?.length ?? 0) > 0,
                      )
                    "
                    @click.stop="handleFilterClick('authors')"
                  />
                </template>
                <UniversalFilterTree
                  field-key="authors"
                  module="document"
                  item-children-key="children"
                  :store="docsFilter"
                  :model-value="tempSelectedFilters.authors || []"
                  placeholder="Поиск: Разработчик"
                  @update:model-value="tempSelectedFilters.authors = $event"
                  @apply="applyFilter('authors')"
                />
              </VMenu>
            </span>
          </template>
          <template #header.section>
            <span class="th-content d-flex align-center">
              <span>Разделы нормативов</span>
              <div
                v-if="docsFilter.filterLoading.sections"
                class="d-flex align-center justify-center ml-1"
                style="width: 28px; height: 28px"
              >
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="20"
                  width="2"
                />
              </div>
              <VMenu
                v-else
                v-model="filterMenus.sections"
                :close-on-content-click="false"
                location="bottom start"
                transition="scale-transition"
              >
                <template #activator="{ props: menuProps }">
                  <VIconBtn
                    v-bind="menuProps"
                    variant="plain"
                    size="small"
                    class="filter-icon-btn ml-1"
                    :icon="
                      getFilterIcon(
                        (docsFilter.filters.sections?.length ?? 0) > 0,
                      )
                    "
                    @click.stop="handleFilterClick('sections')"
                  />
                </template>
                <UniversalFilterTree
                  field-key="sections"
                  module="document"
                  item-children-key="children"
                  :store="docsFilter"
                  :model-value="tempSelectedFilters.sections || []"
                  placeholder="Поиск: Разделы нормативов"
                  @update:model-value="tempSelectedFilters.sections = $event"
                  @apply="applyFilter('sections')"
                />
              </VMenu>
            </span>
          </template>
          <template #header.approved_at>
            <span class="th-content d-flex align-center">
              <span
                class="sortable-title d-flex align-center"
                @click="toggleColumnSort('approved_at')"
              >
                <span>Дата утверждения</span>
                <VIcon
                  :icon="getSortIcon('approved_at')"
                  size="16"
                  class="ml-1"
                />
              </span>
              <DateFilterDropdown
                :date-from="activeFilters.dateApprovedFrom"
                :date-to="activeFilters.dateApprovedTo"
                :active="isDateApprovedFilterActive"
                @apply="handleDateApprovedFilterApply"
              />
            </span>
          </template>
          <template #header.valid_until>
            <span class="th-content d-flex align-center">
              <span
                class="sortable-title d-flex align-center"
                @click="toggleColumnSort('valid_until')"
              >
                <span>Дата истечения</span>
                <VIcon
                  :icon="getSortIcon('valid_until')"
                  size="16"
                  class="ml-1"
                />
              </span>
              <DateFilterDropdown
                :date-from="activeFilters.dateValidUntilFrom"
                :date-to="activeFilters.dateValidUntilTo"
                :active="isDateValidUntilFilterActive"
                @apply="handleDateValidUntilFilterApply"
              />
            </span>
          </template>
          <template #item.checkbox="{ item }">
            <div
              v-if="item.id !== PARENT_NAV_ID"
              class="col-checkbox"
              @click.stop
            >
              <VCheckbox
                :model-value="isItemSelected(item.id)"
                hide-details
                density="compact"
                @update:model-value="toggleDocumentSelection(item)"
              />
            </div>
          </template>
          <template #item.icon="{ item }">
            <div
              class="col-icon"
              @click.stop="handleIconClick(item, $event)"
            >
              <VIcon
                v-if="item.id === PARENT_NAV_ID"
                icon="ri-corner-left-up-line"
                size="20"
                color="#8A8D93"
              />
              <VIcon
                v-else-if="item.is_dir"
                icon="ri-folder-line"
                size="20"
                color="#8A8D93"
              />
              <VTooltip
                v-else
                text="Карточка документа"
              >
                <template #activator="{ props: tooltipProps }">
                  <svg
                    v-bind="tooltipProps"
                    width="18"
                    height="20"
                    viewBox="0 0 18 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="doc-icon mt-1"
                    @click.stop="handleIconClick(item, $event)"
                  >
                    <path
                      d="M5.595 10.812C5.46821 10.2784 5.46821 9.72257 5.595 9.189L4.603 8.616L5.603 6.884L6.595 7.457C6.99316 7.07988 7.47446 6.80172 8 6.645V5.5H10V6.645C10.532 6.803 11.012 7.085 11.405 7.457L12.397 6.884L13.397 8.616L12.405 9.189C12.5316 9.72225 12.5316 10.2777 12.405 10.811L13.397 11.384L12.397 13.116L11.405 12.543C11.0068 12.9201 10.5255 13.1983 10 13.355V14.5H8V13.355C7.47446 13.1983 6.99316 12.9201 6.595 12.543L5.603 13.116L4.603 11.384L5.595 10.812ZM9 11.5C9.39782 11.5 9.77936 11.342 10.0607 11.0607C10.342 10.7794 10.5 10.3978 10.5 10C10.5 9.60218 10.342 9.22064 10.0607 8.93934C9.77936 8.65804 9.39782 8.5 9 8.5C8.60218 8.5 8.22064 8.65804 7.93934 8.93934C7.65804 9.22064 7.5 9.60218 7.5 10C7.5 10.3978 7.65804 10.7794 7.93934 11.0607C8.22064 11.342 8.60218 11.5 9 11.5ZM12 2H2V18H16V6H12V2ZM0 0.992C0 0.444 0.447 0 0.999 0H13L18 5V18.993C18.0009 19.1243 17.976 19.2545 17.9266 19.3762C17.8772 19.4979 17.8043 19.6087 17.7121 19.7022C17.6199 19.7957 17.5101 19.8701 17.3892 19.9212C17.2682 19.9723 17.1383 19.9991 17.007 20H0.993C0.730378 19.9982 0.479017 19.8931 0.293218 19.7075C0.107418 19.5219 0.00209465 19.2706 0 19.008V0.992Z"
                      fill="#8A8D93"
                    />
                  </svg>
                </template>
              </VTooltip>
            </div>
          </template>
          <template #item.name="{ item }">
            <template v-if="item.id === PARENT_NAV_ID">
              {{ " " }}
            </template>
            <template v-else-if="item.is_dir">
              <span class="folder-row-name"><DocumentSearchText :parts="item.search_name_parts" :text="item.name" /></span>
            </template>
            <template v-else>
              <div class="document-row-name">
                <DocumentSearchText :parts="item.search_name_parts" :text="item.name" />
              </div>
              <div v-if="item.search_context_parts?.length" class="document-search-context">
                <span v-if="item.search_context_source === 'semantic'" class="document-search-context-label">По смыслу: </span>
                <DocumentSearchText :parts="item.search_context_parts" />
              </div>
            </template>
          </template>
          <template #item.number="{ item }">
            <template v-if="item.id === PARENT_NAV_ID || item.is_dir">
              {{ " " }}
            </template>
            <template v-else>
              <DocumentSearchText :parts="item.search_number_parts" :text="item.number || '—'" />
            </template>
          </template>
          <template #item.edition="{ item }">
            {{ displayCell(item, getDocumentEdition(item)) }}
          </template>
          <template #item.doc_type="{ item }">
            {{ displayCell(item, item.type?.name) }}
          </template>
          <template #item.status="{ item }">
            <template v-if="item.is_dir">
              {{ " " }}
            </template>
            <VChip
              v-else-if="getDocumentStatusDisplay(item)"
              size="small"
              variant="flat"
              class="status-chip"
              :style="{
                backgroundColor: getDocumentStatusDisplay(item)?.bgColor ?? '',
                color: getDocumentStatusDisplay(item)?.textColor ?? '',
              }"
            >
              {{ getDocumentStatusDisplay(item)?.label ?? "—" }}
            </VChip>
            <span v-else>—</span>
          </template>
          <template #item.author="{ item }">
            {{ displayCell(item, formatDocumentAuthors(item)) }}
          </template>
          <template #item.created_by="{ item }">
            {{ displayCell(item, formatDocumentPersonShort(item.created_by)) }}
          </template>
          <template #item.section="{ item }">
            {{ displayCell(item, item.sections_content) }}
          </template>
          <template #item.approved_at="{ item }">
            {{ displayCell(item, formatDate(item.approved_at)) }}
          </template>
          <template #item.valid_until="{ item }">
            {{ displayCell(item, formatDate(item.valid_until)) }}
          </template>
          <template #item.created_at="{ item }">
            {{ displayCell(item, formatDate(item.created_at)) }}
          </template>
          <template #item.effective_at="{ item }">
            {{ displayCell(item, formatDate(item.effective_at ?? null)) }}
          </template>
          <template #no-data>
            <div class="pa-6 text-center text-medium-emphasis">
              {{ documentsError ? "Ошибка загрузки" : "Документы не найдены" }}
            </div>
          </template>
          <template #bottom>
            <div class="custom-data-table-footer">
              <VDataTableFooter
                show-current-page
                :items-per-page-options="[25, 50, 100]"
              />
            </div>
          </template>
        </VDataTableServer>
      </div>
    </main>

    <DocumentFormSidebar
      v-if="isAddModalOpen"
      key="create-document"
      v-model:is-open="isAddModalOpen"
      mode="create"
      :folders="foldersWithoutGlobal"
      :initial-folder-id="selectedFolderId"
      @created="handleDocumentCreated"
    />

    <DocumentFormSidebar
      v-if="isEditModalOpen"
      :key="`edit-document-${documentToEdit}`"
      v-model:is-open="isEditModalOpen"
      mode="edit"
      :document-id="documentToEdit"
      :folders="folders"
      :can-edit="canEditDocuments && !isInGlobalLibrary"
      @updated="handleDocumentUpdated"
      @deleted="handleDocumentDeleted"
    />

    <DocumentMoveModal
      ref="moveModalRef"
      :folders="foldersWithoutGlobal"
      :documents-count="documentsToMoveCount"
      :title="moveModalTitle"
      @move="confirmMove"
      @cancel="closeMoveModal"
    />

    <FolderNameModal
      v-model:is-open="isFolderNameModalOpen"
      :mode="folderNameMode"
      :initial-name="folderNameInitial"
      :parent-name="folderNameParent"
      :loading="isFolderSaving"
      @submit="handleFolderNameSubmit"
    />

    <DocumentTypesManageModal v-model:is-open="isDocumentTypeModalOpen" />

    <VDialog
      v-model="isDeleteModalOpen"
      max-width="532"
      persistent
    >
      <VCard class="pa-5">
        <VCardTitle class="text-h5 font-weight-bold mb-3">
          {{ getDeleteMessage.title }}
        </VCardTitle>

        <VCardText class="text-body-1">
          {{ getDeleteMessage.text }}
        </VCardText>

        <VCardActions class="mt-5 justify-end gap-4">
          <VBtn
            variant="outlined"
            color="grey"
            class="px-6"
            @click="closeDeleteModal"
          >
            Отменить
          </VBtn>

          <VBtn
            variant="elevated"
            color="primary"
            class="px-6"
            :loading="isDocumentsLoading || isDeleting"
            @click="confirmDelete"
          >
            Удалить
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <ColumnSettingsModal
      v-model="isColumnSettingsOpen"
      :columns="allColumns"
      :visible-keys="visibleKeys"
      :show-folders="showFolders"
      @save="onColumnSettingsSave"
    />
    <ImportHistoryModal
      v-model="importHistoryOpen"
      import-type="documents"
    />
  </div>
</template>

<style scoped lang="scss">
.documents-new-container {
  display: flex;
  flex-direction: row !important;
  align-items: stretch;
  height: calc(100vh - 120px);
  min-height: 0;
  background: rgb(var(--v-theme-surface));
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;

  @media (max-width: 1023px) {
    height: calc(100vh - 80px);
    border-radius: 0;
  }
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.sidebar {
  width: 360px;
  min-width: 0;
  min-height: 0;
  background: rgb(var(--v-theme-surface));
  border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  overflow-x: hidden;
  overflow-y: auto;
  flex: 0 0 auto;
  transition: transform 0.3s ease;

  &.sidebar-mobile {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(360px, 85vw);
    z-index: 1000;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease;
  }
}

.documents-new-container > :deep(.entity-form-resizer) {
  align-self: stretch;
  flex-shrink: 0;
}

.sidebar-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgb(var(--v-theme-surface));
  position: sticky;
  top: 0;
  z-index: 1;

  .sidebar-title {
    font-size: 18px;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.87);
  }
}

@keyframes slideIn {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

.menu-toggle-btn {
  flex-shrink: 0;
}

.sidebar-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 16px;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 14px;
}

.folders-tree-wrapper {
  padding: 12px 0;
}

.folders-tree-wrapper :deep(.folders-tree) {
  margin: 0;
}

.folders-tree-wrapper :deep(.folders-tree-content) {
  padding: 0;
}

.main-content {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  min-width: 0;
  min-height: 0;
}

.toolbar-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 24px;
  gap: 12px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);

  @media (max-width: 1023px) {
    padding: 12px 16px;
    gap: 8px;
  }
}

.toolbar-header-actions {
  flex: 1 1 auto;
  min-width: 0;
}

.search-field {
  flex: 1 1 auto;
  min-width: 160px;

  :deep(.v-field) {
    background: rgb(var(--v-theme-surface));
    box-shadow: none;
    border: none;
    padding-left: 0 !important;
  }

  :deep(.v-field__input) {
    padding-top: 6px;
    padding-bottom: 6px;
    font-size: 14px;
    color: rgba(var(--v-theme-on-surface), 0.87);
  }

  :deep(.v-field__input::placeholder) {
    color: rgba(var(--v-theme-on-surface), 0.4);
    opacity: 1;
  }

  :deep(.v-field__prepend-inner) {
    padding-left: 4px;
    padding-right: 4px;
  }

  :deep(.v-field__prepend-inner .v-icon) {
    opacity: 0.4;
    font-size: 20px;
  }
}

.add-button {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  padding: 0 16px;
  flex-shrink: 0;

  @media (max-width: 1023px) {
    padding: 0 12px;
  }
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  min-height: 54px;
  flex-shrink: 0;

  :deep(.v-btn:not(.v-btn--disabled)) {
    cursor: pointer;
  }

  :deep(.v-btn--disabled) {
    cursor: not-allowed;
  }

  @media (max-width: 1023px) {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.actions-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.actions-right {
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 1023px) {
    .v-btn {
      font-size: 12px;
      padding: 0 8px;
    }
  }
}

.action-icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 4px;
  cursor: pointer;

  &:disabled,
  &.v-btn--disabled {
    cursor: not-allowed;
  }

  :deep(.v-icon) {
    font-size: 20px;
  }
}

.action-btn-wrap {
  cursor: pointer;

  &:has(.v-btn--disabled) {
    cursor: not-allowed;
  }
}

.menu-icon {
  color: rgba(var(--v-theme-on-surface), 0.7);
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: rgba(var(--v-theme-on-surface), 0.9);
  }
}

.filter-chips-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  flex-wrap: wrap;
  flex-shrink: 0;
  overflow-x: auto;

  @media (max-width: 1023px) {
    padding: 12px 16px;
  }
}

.filter-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  min-height: fit-content !important;
}

.documents-table-wrapper {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  padding: 0;
  position: relative;

  :deep(.v-data-table) {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }

  :deep(.v-table__wrapper) {
    flex: 1 1 0;
    min-height: 0;
    overflow: auto !important;
  }

  :deep(.table-hscroll) {
    display: none !important;
  }

  :deep(thead .v-data-table__th),
  :deep(thead th) {
    position: sticky !important;
    inset-block-start: 0 !important;
    top: 0 !important;
    z-index: 2 !important;
    background-color: rgb(var(--v-theme-surface)) !important;
  }

  :deep(.v-data-table > .v-table__wrapper ~ *) {
    position: static !important;
    inset-block-end: auto !important;
  }
}

.table-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  height: 100%;
  text-align: center;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 16px;
}

.custom-data-table-footer {
  flex-shrink: 0;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);

  :deep(.v-data-table-footer) {
    display: flex !important;
    justify-content: center !important;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }
    .v-data-table-footer__items-per-page {
      order: 2;
    }
    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }
    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
    .v-pagination__first > button[aria-disabled="true"] {
      display: none !important;
    }
    .v-pagination__prev > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}

.documents-table {
  width: 100%;

  @media (max-width: 1023px) {
    min-width: 800px;
  }

  :deep(th:nth-child(1)),
  :deep(td:nth-child(1)) {
    width: 56px;
    min-width: 56px;
    max-width: 56px;
    padding: 8px 12px !important;
    box-sizing: border-box;
  }

  :deep(th:nth-child(2)),
  :deep(td:nth-child(2)) {
    width: 56px;
    min-width: 56px;
    max-width: 56px;
    padding: 8px 12px !important;
    box-sizing: border-box;
  }

  .folder-row-name {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: rgba(var(--v-theme-on-surface), 0.87);
    white-space: nowrap;
  }

  .document-search-context {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    max-block-size: 54px;
    overflow: hidden;
    margin-block-start: 5px;
    color: rgba(var(--v-theme-on-surface), 0.65);
    font-size: 12px;
    line-height: 18px;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .document-search-context-label {
    font-weight: 500;
  }

  .document-row-name {
    display: inline-block;
    max-inline-size: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  :deep(.v-data-table__th) {
    background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    letter-spacing: 0 !important;
    line-height: 22px !important;
    position: relative;
    padding-inline: 1rem;
    border-bottom: none;
    white-space: nowrap;
    height: 46px;
    vertical-align: middle;
  }

  :deep(.v-data-table__th:not(:last-child))::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    width: 2px;
    height: 14px;
    background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
  }

  .th-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .sortable-title {
    cursor: pointer;
    user-select: none;
  }

  .filter-icon {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: rgba(var(--v-theme-on-surface), 1);
    }
  }

  :deep(tbody) {
    tr {
      cursor: pointer;
      background-color: rgb(var(--v-theme-surface));

      &.folder-row {
        background-color: rgba(var(--v-theme-on-surface), 0.02);
      }

      &.document-row--selected {
        background-color: rgba(var(--v-theme-primary), 0.04);
      }
    }

    td {
      padding: 12px 16px;
      font-size: 14px;
      color: rgba(var(--v-theme-on-surface), 0.87);
      border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    }
  }

  :deep(.v-data-table__tr):hover {
    position: relative;
    z-index: 1;
    box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
    cursor: pointer;
  }

  &.dark-theme :deep(.v-data-table__tr:hover) {
    box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
  }

  .col-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .col-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    :deep(.v-icon) {
      cursor: pointer;
    }

    svg {
      cursor: pointer;
    }
  }

  .col-name {
    width: 560px;
    min-width: 500px;
    max-width: 560px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .col-type {
    width: 200px;
  }

  .col-author,
  .col-creator {
    width: 200px;
  }

  .col-created {
    width: 180px;
  }

  .col-section {
    min-width: 200px;
  }
}

.doc-icon {
  color: rgba(var(--v-theme-on-surface), 0.6);
  transition: opacity 0.2s;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.status-chip {
  block-size: 22px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: normal;
  text-transform: none;

  :deep(.v-chip__underlay),
  :deep(.v-chip__overlay) {
    opacity: 0;
  }
}

:deep(.v-checkbox) {
  .v-selection-control {
    min-height: auto;
  }

  .v-selection-control__wrapper {
    height: 20px;
  }
}

:deep(.v-dialog .v-card) {
  border-radius: 12px;
}

:deep(.v-dialog .v-btn) {
  text-transform: none;
  font-weight: 500;
}
</style>
